import React, { useCallback, useEffect } from "react";
import { useMutation } from "react-query";
import Axios from "axios";
import {
    antdAsyncMessageError,
    antdAsyncMessageSuccess,
} from "../../../../../helpers/AsyncMessage";
import { Form, Input, Button, Select, Upload, DatePicker } from "antd";
import ModalSpecial from "../../../../../components/Layout/Modals/ModalSpecial";
import debounce from 'lodash.debounce';
import { IconImport } from "../../../../../components/Layout/Icons";
import { getDateFormat } from "../../../../../helpers/Utils";


const ModalSetting = ({ onClose, modalOpt, feedSettingsData }) => {
    const [form] = Form.useForm();
    const [brandsOptions, setBrandsOptions] = React.useState([]);
    const [productsOptions, setProductsOptions] = React.useState([]);
    const [categoriesOptions, setCategoriesOptions] = React.useState([]);
    const [categoriesIncludeValue, setCategoriesIncludeValue] = React.useState([]);
    const [categoriesExcludeValue, setCategoriesExcludeValue] = React.useState([]);
    const [brandsIncludeValue, setBrandsIncludeValue] = React.useState([]);
    const [brandsExcludeValue, setBrandsExcludeValue] = React.useState([]);
    const [productsIncludeValue, setProductsIncludeValue] = React.useState([]);
    const [productsExcludeValue, setProductsExcludeValue] = React.useState([]);
    const [validFromDate, setValidFromDate] = React.useState(null);
    const [validToDate, setValidToDate] = React.useState(null);

    useEffect(() => {
        if (modalOpt.type === "edit_setting" && feedSettingsData?.length) {
            const record = feedSettingsData.find((r) => r.id === modalOpt.record_id);
            if (record) {
                form.setFieldsValue({
                    title: record.title,
                    code: record.code,
                    valid_from: record.valid_from,
                    valid_to: record.valid_to,
                    value: record.value,
                });

                setValidFromDate(getDateFormat(record.valid_from));
                setValidToDate(getDateFormat(record.valid_to));

                const fetchInitialData = async () => {
                    try {
                        if (record.included_brand_ids?.length) {
                            const brandParamsIn = record.included_brand_ids
                                .map(id => `q[id_in][]=${id}`)
                                .join('&');
                            const { data: brandDataIn } = await Axios.get(
                                `/api/v1/brands?${brandParamsIn}`
                            );
                            setBrandsIncludeValue(
                                brandDataIn.map((b) => ({ label: b.title, value: b.id }))
                            );
                        } else {
                            setBrandsIncludeValue([]);
                        }

                        if (record.excluded_brand_ids?.length) {
                            const brandParamsEx = record.excluded_brand_ids
                                .map(id => `q[id_in][]=${id}`)
                                .join('&');
                            const { data: brandDataEx } = await Axios.get(
                                `/api/v1/brands?${brandParamsEx}`
                            );
                            setBrandsExcludeValue(
                                brandDataEx.map((b) => ({ label: b.title, value: b.id }))
                            );
                        } else {
                            setBrandsExcludeValue([]);
                        }

                        if (record.included_category_ids?.length) {
                            const params = {
                                q: {
                                    id_in: record.included_category_ids
                                },
                                page: 1,
                                per_page: 9999999
                            };
                            const { data: categoriesIn } = await Axios.post(
                                `/api/v1/categories/simple_list`,
                                params
                            );
                            setCategoriesIncludeValue(
                                categoriesIn.map((c) => ({ label: c.name, value: c.id }))
                            );
                        } else {
                            setCategoriesIncludeValue([]);
                        }

                        if (record.excluded_category_ids?.length) {
                            const categoryParamsEx = {
                                q: {
                                    id_in: record.excluded_category_ids
                                },
                                page: 1,
                                per_page: 9999999
                            };
                            const { data: categoriesEx } = await Axios.post(
                                `/api/v1/categories/simple_list`,
                                categoryParamsEx
                            );
                            setCategoriesExcludeValue(
                                categoriesEx.map((c) => ({ label: c.name, value: c.id }))
                            );
                        } else {
                            setCategoriesExcludeValue([]);
                        }

                        if (record.included_product_ids?.length) {
                            const productParamsIn = {
                                q: {
                                    id_in: record.included_product_ids
                                },
                                page: 1,
                                per_page: 9999999
                            };
                            const { data: productsIn } = await Axios.post(
                                `/api/v1/products/simple_list`,
                                productParamsIn
                            );
                            const mappedIn = productsIn.data?.map((p) => ({ label: p.title, value: p.id })) || [];
                            setProductsIncludeValue(mappedIn);
                        } else {
                            setProductsIncludeValue([]);
                        }

                        if (record.excluded_product_ids?.length) {
                            const productParamsExcl = {
                                q: {
                                    id_in: record.excluded_product_ids
                                },
                                page: 1,
                                per_page: 9999999
                            };
                            const { data: productsEx } = await Axios.post(
                                `/api/v1/products/simple_list`,
                                productParamsExcl
                            );
                            const mappedEx = productsEx.data?.map((p) => ({ label: p.title, value: p.id })) || [];
                            setProductsExcludeValue(mappedEx);
                        } else {
                            setProductsExcludeValue([]);
                        }
                    } catch (err) {
                        console.error(err);
                    }
                };

                fetchInitialData();
            }
        }
    }, [modalOpt, feedSettingsData, form]);

    const uploadProps = (setProductIds, type = 'include') => ({
        name: 'file',
        action: '/api/v1/feeds/product_ids?type=' + type + "&feed_setting_id=" + modalOpt.record_id,
        accept: '.xlsx',
        showUploadList: false,
        onChange(info) {
            if (info.file.status === 'uploading') {
                return;
            }
            if (info.file.status === 'done') {
                const productIds = info.file.response;

                const fetchProductDetails = async () => {
                    try {
                        const params = {
                            q: {
                                id_in: productIds
                            },
                            page: 1,
                            per_page: 9999999
                        };
                        const { data: { data: products } } = await Axios.post(`/api/v1/products/simple_list`, params);

                        const formattedProducts = products.map(product => ({
                            label: product.title,
                            value: product.id
                        }));

                        setProductIds(formattedProducts);
                        antdAsyncMessageSuccess({ text: "Products imported successfully", key: "xlsx-import" });
                    } catch (error) {
                        console.error('Error fetching product details:', error);
                        // Fallback to basic format if fetch fails
                        const fallbackFormat = productIds.map(id => ({
                            label: `Product ${id}`,
                            value: id
                        }));
                        setProductIds(fallbackFormat);
                        antdAsyncMessageError({ text: "Couldn't fetch product details", key: "xlsx-import" });
                    }
                };

                fetchProductDetails();
            } else if (info.file.status === 'error') {
                antdAsyncMessageError({ text: "Failed to import products", key: "xlsx-import" });
            }
        },
    });

    const fetchBrandsOptions = async (brand) => {
        const { data } = await Axios.get(`/api/v1/brands?q[title_i_cont_any]=${brand}`);
        const options = data.map((item) => ({ label: item.title, value: item.id }));
        setBrandsOptions(() => options);
    };

    const fetchProductsOptions = async (product_code) => {
        const { data } = await Axios.get(
            `/api/v1/products?page=1&per_page=20&q[identifier_eq]=${product_code}`
        );
        const options = data.data.map((item) => ({ label: item.title, value: item.id }));
        setProductsOptions(() => options);
    };

    const fetchCategoriesOptions = async (category) => {
        const { data } = await Axios.get(
            `/api/v1/categories/simple_list?page=1&per_page=30&q[main_true]=1&q[name_cont]=${category}`
        );
        const options = data.map((item) => ({ label: item.name, value: item.id }));
        setCategoriesOptions(() => options);
    };


    const mutationPOST = useMutation({
        mutationFn: (feed) => {
            return Axios.post(`/api/v1/feed_settings`, {
                feed_setting: {
                    title: feed.title,
                    value: feed.value,
                    code: feed.code,
                    valid_from: validFromDate,
                    valid_to: validToDate,
                    order_shop_id: feed.order_shop_id,
                    excluded_brand_ids: brandsExcludeValue.map((b) => b.value),
                    excluded_category_ids: categoriesExcludeValue.map((c) => c.value),
                    excluded_product_ids: productsExcludeValue.map((p) => p.value),
                    included_category_ids: categoriesIncludeValue.map((c) => c.value),
                    included_brand_ids: brandsIncludeValue.map((b) => b.value),
                    included_product_ids: productsIncludeValue.map((p) => p.value),
                },
            }).then((res) => {
                onClose();
                antdAsyncMessageSuccess({ text: "Successfully created", key: "default" });
            }).catch((err) => {
                if (err?.response?.data?.message && typeof err.response.data.message === 'object') {
                    const parsedErrors = Object.entries(err.response.data.message).map(([field, messages]) => {
                        if (Array.isArray(messages)) {
                            return `${field}: ${messages.join(', ')}`;
                        } else {
                            return `${field}: ${messages}`;
                        }
                    });
                    antdAsyncMessageError({ text: parsedErrors.join('\n'), key: "default" });
                } else {
                    antdAsyncMessageError({ text: JSON.stringify(err?.response?.data?.message) || "Failed to update", key: "default" });
                }
            });
        }
    });

    const mutationPUT = useMutation({
        mutationFn: (feed) => {
            return Axios.put(`/api/v1/feed_settings/${modalOpt.record_id}`, {
                feed_setting: {
                    title: feed.title,
                    value: feed.value,
                    code: feed.code,
                    valid_from: validFromDate,
                    valid_to: validToDate,
                    order_shop_id: feed.order_shop_id,
                    excluded_brand_ids: brandsExcludeValue.map((b) => b.value),
                    excluded_category_ids: categoriesExcludeValue.map((c) => c.value),
                    excluded_product_ids: productsExcludeValue.map((p) => p.value),
                    included_category_ids: categoriesIncludeValue.map((c) => c.value),
                    included_brand_ids: brandsIncludeValue.map((b) => b.value),
                    included_product_ids: productsIncludeValue.map((p) => p.value),
                },
            }).then((res) => {
                onClose();
                antdAsyncMessageSuccess({ text: "Successfully created", key: "default" });
            }).catch((err) => {
                if (err?.response?.data?.message && typeof err.response.data.message === 'object') {
                    const parsedErrors = Object.entries(err.response.data.message).map(([field, messages]) => {
                        if (Array.isArray(messages)) {
                            return `${field}: ${messages.join(', ')}`;
                        } else {
                            return `${field}: ${messages}`;
                        }
                    });
                    antdAsyncMessageError({ text: parsedErrors.join('\n'), key: "default" });
                } else {
                    antdAsyncMessageError({ text: JSON.stringify(err?.response?.data?.message) || "Failed to update", key: "default" });
                }
            });
        }
    });

    const debouncedFetchBrandsOptions = useCallback(debounce(fetchBrandsOptions, 500), []);
    const debouncedFetchProductsOptions = useCallback(debounce(fetchProductsOptions, 500), []);
    const debouncedFetchCategoriesOptions = useCallback(debounce(fetchCategoriesOptions, 500), []);

    return (
        <ModalSpecial
            style={{ width: 570 }}
            text={
                <span className="font-bold text-xl text-black">
                    {modalOpt.type === "edit_setting" ? "Edit" : "New"} setting
                </span>
            }
            onClose={onClose}
        >
            <Form
                form={form}
                layout="vertical"
                name="basic"
                onFinish={(values) => {
                    modalOpt.type === "edit_setting" ? mutationPUT.mutate(values) : mutationPOST.mutate(values);
                }}
                autoComplete="off"
            >
                <Form.Item
                    label="Title"
                    name="title"
                    style={{ marginBottom: 0 }}
                    rules={[{ required: true, message: "Can't be empty" }]}
                >
                    <Input placeholder="Type in" />
                </Form.Item>
                <Form.Item
                    label="Code"
                    name="code"
                    style={{ marginBottom: 0 }}
                >
                    <Input placeholder="Type in" />
                </Form.Item>
                <Form.Item
                    label="Value"
                    name="value"
                    style={{ marginBottom: 0 }}
                    rules={[{ required: true, message: "Can't be empty" }]}
                >
                    <Input placeholder="Type in" />
                </Form.Item>

                <Form.Item label="Categories" name="categories" style={{ marginBottom: 0 }}>
                    <div
                        className="p-2"
                        style={{
                            backgroundColor: "#F5F5F5",
                            border: "1px solid #EFEFEF",
                            borderRadius: 1,
                        }}
                    >
                        <div className="text-xs">Include</div>
                        <Select
                            mode="multiple"
                            options={categoriesOptions}
                            value={categoriesIncludeValue}
                            dropdownMatchSelectWidth={false}
                            labelInValue
                            showSearch
                            filterOption={false}
                            onChange={(value) => setCategoriesIncludeValue(value)}
                            onSearch={debouncedFetchCategoriesOptions}
                        />
                        <div className="text-xs">Exclude</div>
                        <Select
                            mode="multiple"
                            options={categoriesOptions}
                            value={categoriesExcludeValue}
                            dropdownMatchSelectWidth={false}
                            labelInValue
                            showSearch
                            filterOption={false}
                            onChange={(value) => setCategoriesExcludeValue(value)}
                            onSearch={debouncedFetchCategoriesOptions}
                        />
                    </div>
                </Form.Item>
                <Form.Item label="Products" name="products" style={{ marginBottom: 0 }}>
                    <div
                        className="p-2"
                        style={{
                            backgroundColor: "#F5F5F5",
                            border: "1px solid #EFEFEF",
                            borderRadius: 1,
                        }}
                    >
                        <div className="text-xs">Include</div>
                        <Select
                            mode="multiple"
                            options={productsOptions}
                            value={productsIncludeValue}
                            dropdownMatchSelectWidth={false}
                            labelInValue
                            showSearch
                            filterOption={false}
                            onChange={(value) => setProductsIncludeValue(value)}
                            onSearch={debouncedFetchProductsOptions}
                        />
                        <Upload {...uploadProps(setProductsIncludeValue, 'include')}>
                            <Button
                                icon={<IconImport />}
                                size="small"
                                type="link"
                                className="text-primary p-0"
                            >
                                Import XLSX
                            </Button>
                        </Upload>
                        <div className="text-xs">Exclude</div>
                        <Select
                            mode="multiple"
                            options={productsOptions}
                            value={productsExcludeValue}
                            dropdownMatchSelectWidth={false}
                            labelInValue
                            showSearch
                            filterOption={false}
                            onChange={(value) => setProductsExcludeValue(value)}
                            onSearch={debouncedFetchProductsOptions}
                        />
                        <Upload {...uploadProps(setProductsExcludeValue, 'exclude')}>
                            <Button
                                icon={<IconImport />}
                                size="small"
                                type="link"
                                className="text-primary p-0"
                            >
                                Import XLSX
                            </Button>
                        </Upload>
                    </div>
                </Form.Item>
                <Form.Item label="Brands" name="brands">
                    <div
                        className="p-2"
                        style={{
                            backgroundColor: "#F5F5F5",
                            border: "1px solid #EFEFEF",
                            borderRadius: 1,
                        }}
                    >
                        <div className="text-xs">Include</div>
                        <Select
                            mode="multiple"
                            options={brandsOptions}
                            value={brandsIncludeValue}
                            dropdownMatchSelectWidth={false}
                            labelInValue
                            showSearch
                            filterOption={false}
                            onChange={(value) => setBrandsIncludeValue(value)}
                            onSearch={debouncedFetchBrandsOptions}
                        />
                        <div className="text-xs">Exclude</div>
                        <Select
                            mode="multiple"
                            options={brandsOptions}
                            value={brandsExcludeValue}
                            dropdownMatchSelectWidth={false}
                            labelInValue
                            showSearch
                            filterOption={false}
                            onChange={(value) => setBrandsExcludeValue(value)}
                            onSearch={debouncedFetchBrandsOptions}
                        />
                    </div>
                </Form.Item>

                <Form.Item
                    label="Valid period"
                    style={{ marginTop: 15, marginBottom: 0 }}
                >
                    <DatePicker.RangePicker
                        style={{ width: "100%" }}
                        placeholder={["Start date", "End date"]}
                        format="YYYY-MM-DD"
                        value={[validFromDate, validToDate]}
                        onChange={(dates, dateStrings) => {
                            form.setFieldsValue({
                                valid_from: dateStrings[0] || null,
                                valid_to: dateStrings[1] || null
                            });
                            setValidFromDate(dates?.[0] || null);
                            setValidToDate(dates?.[1] || null);
                        }}
                        renderExtraFooter={() => (
                            <div className="d-flex align-items-center justify-content-center">
                                {!!validFromDate && (
                                    <div
                                        className="text-primary py-2 cursor-pointer"
                                        onClick={() => {
                                            form.setFieldsValue({
                                                valid_to: null
                                            });
                                            setValidToDate(null);
                                        }}
                                    >
                                        Do not set end date
                                    </div>
                                )}
                            </div>
                        )}
                    />
                </Form.Item>
                <Button className="w-100 mt-2" type="primary" htmlType="submit" loading={modalOpt.type === "edit_setting" ? mutationPUT.isLoading : mutationPOST.isLoading}>
                    {modalOpt.type === "edit_setting" ? "Update" : "Add"}
                </Button>
            </Form>
        </ModalSpecial>
    );
};

export default ModalSetting;